import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  tokens,
  Dropdown,
  Option,
  Spinner,
  TabList,
  Tab,
  SelectTabData,
} from '@fluentui/react-components';
import { Search24Regular } from '@fluentui/react-icons';
import Header from '../../components/landing/Header';
import Footer from '../../components/landing/Footer';
import PageHero from '../../components/layout/PageHero';
import { useLicense } from '../../hooks/useLicense';
import { useSearchIndexer } from '../../hooks/useSearchIndexer';
import { coreSettingsService } from '../../services/CoreSettingsService';
import { SEARCH_INDEX_STALE_DAYS } from '../../config/constants';
import { RegistrySetupWizard } from './RegistrySetupWizard';
import { IndexerStatusCard } from './IndexerStatusCard';
import { SourcesPanel } from './SourcesPanel';
import { IndexProgressPanel } from './IndexProgressPanel';
import { CacheManagementPanel } from './CacheManagementPanel';

type TabValue = 'status' | 'sources' | 'index' | 'cache';

const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f0f4f8',
  },
  siteRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '4px',
  },
  siteLabel: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
  },
  main: {
    flex: '1',
    maxWidth: '1280px',
    margin: '0 auto',
    width: '100%',
    padding: '28px 20px 56px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  outerFrame: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '16px',
    padding: '16px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  tabsRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  noSite: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '48px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
});

export default function SearchIndexerPage() {
  const navigate = useNavigate();
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const { license, isLoading: licenseLoading } = useLicense();

  const userName =
    accounts[0]?.name || accounts[0]?.username?.split('@')[0] || 'User';
  const siteUrls: string[] = license?.allowedSiteUrls ?? [];

  const [selectedSiteUrl, setSelectedSiteUrl] = useState<string>('');
  useEffect(() => {
    if (!selectedSiteUrl && siteUrls.length > 0) {
      setSelectedSiteUrl(siteUrls[0]);
    }
  }, [siteUrls, selectedSiteUrl]);

  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const initialTab: TabValue =
    requestedTab === 'sources' || requestedTab === 'index' || requestedTab === 'cache'
      ? requestedTab
      : 'status';
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);
  const [forceFullView, setForceFullView] = useState(false);

  const ctx = useSearchIndexer(selectedSiteUrl);

  const [staleDays, setStaleDays] = useState<number>(SEARCH_INDEX_STALE_DAYS);
  useEffect(() => {
    if (!selectedSiteUrl) return;
    coreSettingsService.getSettings(selectedSiteUrl).then(config => {
      setStaleDays(config.searchIndexStaleDays ?? SEARCH_INDEX_STALE_DAYS);
    }).catch(() => {});
  }, [selectedSiteUrl]);

  const handleTabChange = (_: unknown, data: SelectTabData) => {
    setActiveTab(data.value as TabValue);
  };

  const displaySiteUrl = (url: string) => {
    try {
      return (
        new URL(url).pathname.split('/').filter(Boolean).join(' / ') || url
      );
    } catch {
      return url;
    }
  };

  // Show wizard when registry doesn't exist OR sources are empty (and not loading)
  // unless admin explicitly skipped to full view
  const showWizard =
    !forceFullView &&
    selectedSiteUrl &&
    !ctx.sourcesLoading &&
    ctx.registryExists !== null &&
    (ctx.registryExists === false || ctx.sources.length === 0);

  const handleSkipToFullView = () => {
    setForceFullView(true);
    setActiveTab('index');
  };

  return (
    <div className={styles.page}>
      <Header
        isAuthenticated={true}
        userName={userName}
        onSignOut={() => instance.logoutPopup()}
        siteUrl={selectedSiteUrl || siteUrls[0]}
      />

      <PageHero
        icon={<Search24Regular style={{ width: 22, height: 22 }} />}
        title='Search Indexer'
        subtitle='Build and manage semantic search embeddings for your SharePoint content.'
        breadcrumbs={[
          { label: 'Dashboard', onClick: () => navigate('/dashboard') },
          { label: 'Search Indexer' },
        ]}
        belowSubtitle={
          siteUrls.length > 1 && (
            <div className={styles.siteRow}>
              <span className={styles.siteLabel}>Site:</span>
              <Dropdown
                value={selectedSiteUrl}
                onOptionSelect={(_, d) => {
                  setSelectedSiteUrl(d.optionValue as string);
                  setForceFullView(false);
                }}
                style={{ minWidth: 320 }}
              >
                {siteUrls.map((url) => (
                  <Option key={url} value={url}>
                    {displaySiteUrl(url)}
                  </Option>
                ))}
              </Dropdown>
            </div>
          )
        }
      />

      <main className={styles.main}>
        {/* No site configured */}
        {!selectedSiteUrl && !licenseLoading && (
          <div className={styles.noSite}>
            No SharePoint sites are configured on your license.
            <br />
            Contact your Injio administrator.
          </div>
        )}

        {/* Loading */}
        {!selectedSiteUrl && licenseLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '48px',
            }}
          >
            <Spinner label='Loading license…' />
          </div>
        )}

        {/* Guided setup wizard — shown when registry missing or no sources */}
        {selectedSiteUrl && showWizard && (
          <RegistrySetupWizard
            ctx={ctx}
            onSkipToFullView={handleSkipToFullView}
          />
        )}

        {/* Sources loading skeleton */}
        {selectedSiteUrl &&
          ctx.sourcesLoading &&
          ctx.registryExists === null && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '48px',
              }}
            >
              <Spinner label='Checking registry…' />
            </div>
          )}

        {/* Full admin view — shown once setup is done (or skipped) */}
        {selectedSiteUrl && !showWizard && !ctx.sourcesLoading && (
          <div className={styles.outerFrame}>
            <div className={styles.tabsRow}>
              <TabList selectedValue={activeTab} onTabSelect={handleTabChange}>
                <Tab value='status'>Status</Tab>
                <Tab value='sources'>
                  Sources
                  {ctx.sources.length > 0 ? ` (${ctx.sources.length})` : ''}
                </Tab>
                <Tab value='index'>Index Run</Tab>
                <Tab value='cache'>Cache</Tab>
              </TabList>
            </div>

            <div className={styles.tabContent}>
              {activeTab === 'status' && <IndexerStatusCard ctx={ctx} staleDays={staleDays} />}
              {activeTab === 'sources' && <SourcesPanel ctx={ctx} />}
              {activeTab === 'index' && <IndexProgressPanel ctx={ctx} />}
              {activeTab === 'cache' && <CacheManagementPanel ctx={ctx} />}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
